import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const SLOTS = [
  { value: "header", label: "Header banner (top)" },
  { value: "home_top", label: "Homepage — above hero" },
  { value: "home_middle", label: "Homepage — between hero and grid" },
  { value: "sidebar", label: "Sidebar" },
  { value: "article_top", label: "Article — top" },
  { value: "article_bottom", label: "Article — bottom" },
  { value: "footer", label: "Footer" },
];

const empty = { title: "", image_url: "", link_url: "", slot: "home_middle", is_active: true };

export default function AdminAds() {
  const { user, loading: authLoading } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ads");
        setAds(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (authLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <p className="text-muted-foreground">Admin only.</p>;

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...empty, ...a }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/ads/${editing.id}`, form);
        setAds((prev) => prev.map((x) => (x.id === data.id ? data : x)));
        toast.success("Updated");
      } else {
        const { data } = await api.post("/ads", form);
        setAds((prev) => [data, ...prev]);
        toast.success("Ad added");
      }
      setOpen(false);
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete ad "${a.title}"?`)) return;
    try {
      await api.delete(`/ads/${a.id}`);
      setAds((prev) => prev.filter((x) => x.id !== a.id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between border-b-2 border-primary pb-4 mb-8">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2 hover:underline">
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
          <div className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground">Ads</div>
          <h1 className="font-kannada-heading text-3xl mt-1">ಜಾಹೀರಾತು ನಿರ್ವಹಣೆ</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-none" data-testid="admin-ads-new">
              <Plus className="w-4 h-4 mr-1" /> ಹೊಸ ಜಾಹೀರಾತು
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-none">
            <DialogHeader>
              <DialogTitle className="font-kannada-heading text-2xl">
                {editing ? "Edit Ad" : "New Ad"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Title / Advertiser</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" data-testid="ad-title" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Image URL (banner)</Label>
                <Input required value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="rounded-none mt-1" data-testid="ad-image" />
                {form.image_url && <img src={form.image_url} alt="" className="mt-2 h-16 w-full object-cover rounded-sm" />}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Click-through URL</Label>
                <Input required type="url" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} className="rounded-none mt-1" placeholder="https://..." data-testid="ad-link" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Slot placement</Label>
                <Select value={form.slot} onValueChange={(v) => setForm({ ...form, slot: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="ad-slot"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SLOTS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label className="text-xs uppercase tracking-widest">Active</Label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">ರದ್ದು</Button>
                <Button type="submit" disabled={saving} className="rounded-none" data-testid="ad-save">
                  {saving ? "..." : "ಉಳಿಸಿ"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-card rounded-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Banner</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Slot</TableHead>
              <TableHead className="w-20">Clicks</TableHead>
              <TableHead className="w-20">Active</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : ads.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No ads yet.</TableCell></TableRow>
            ) : (
              ads.map((a) => (
                <TableRow key={a.id} data-testid={`ad-row-${a.id}`}>
                  <TableCell><img src={a.image_url} alt="" className="w-20 h-10 object-cover rounded-sm" /></TableCell>
                  <TableCell className="text-sm font-semibold">{a.title}</TableCell>
                  <TableCell className="text-xs">
                    <span className="px-2 py-1 bg-muted rounded-sm">{SLOTS.find((s) => s.value === a.slot)?.label || a.slot}</span>
                  </TableCell>
                  <TableCell className="text-sm">{a.clicks || 0}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm ${a.is_active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                      {a.is_active ? "Live" : "Off"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" asChild><a href={a.link_url} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4" /></a></Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(a)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(a)}><Trash2 className="w-4 h-4 text-primary" /></Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
